
import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { KeywordInput } from './KeywordInput';
import { AuthorsInput } from './AuthorsInput';
import { PaperAuthor } from '@/types/paper';
import { categories } from '@/data/exploreConstants';

interface PaperMetadataFormProps {
  title: string;
  setTitle: (title: string) => void;
  abstract: string;
  setAbstract: (abstract: string) => void;
  category: string;
  setCategory: (category: string) => void;
  keywords: string[];
  setKeywords: (keywords: string[]) => void;
  additionalAuthors: PaperAuthor[];
  setAdditionalAuthors: (authors: PaperAuthor[]) => void;
}

export const PaperMetadataForm: React.FC<PaperMetadataFormProps> = ({
  title,
  setTitle,
  abstract,
  setAbstract,
  category,
  setCategory,
  keywords,
  setKeywords,
  additionalAuthors,
  setAdditionalAuthors
}) => {
  const [selectedField, setSelectedField] = React.useState<string>('');

  // Find the field and subcategory from current category
  React.useEffect(() => {
    if (category) {
      const currentCategory = categories.find(cat => 
        cat.id === category || cat.subcategories?.some(sub => sub.id === category)
      );
      if (currentCategory) {
        setSelectedField(currentCategory.id);
      }
    }
  }, [category]);

  // Get available subcategories for selected field
  const selectedFieldData = categories.find(cat => cat.id === selectedField);
  const availableSubcategories = selectedFieldData?.subcategories || [];

  // Handle field selection
  const handleFieldChange = (fieldId: string) => {
    setSelectedField(fieldId);
    const fieldData = categories.find(cat => cat.id === fieldId);
    
    // If field has no subcategories, set it as the final category
    if (!fieldData?.subcategories || fieldData.subcategories.length === 0) {
      setCategory(fieldId);
    } else {
      // Clear category selection when field changes
      setCategory('');
    }
  };

  // Handle specialization selection
  const handleSpecializationChange = (subcategoryId: string) => {
    setCategory(subcategoryId);
  };
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Paper Title</Label>
        <Input 
          id="title" 
          placeholder="Enter the title of your paper" 
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-12"
        />
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label htmlFor="abstract">Abstract</Label>
          <span className={`text-sm ${
            abstract.length < 10 ? 'text-destructive' : 'text-muted-foreground'
          }`}>
            {abstract.length} / 10 minimum
          </span>
        </div>
        <Textarea 
          id="abstract" 
          placeholder="Enter the abstract of your paper (minimum 10 characters)" 
          value={abstract}
          onChange={(e) => setAbstract(e.target.value)}
          rows={6}
          className={`resize-none ${
            abstract.length > 0 && abstract.length < 10 
              ? 'border-destructive focus:border-destructive' 
              : ''
          }`}
        />
        {abstract.length > 0 && abstract.length < 10 && (
          <p className="text-sm text-destructive">
            Please enter at least {10 - abstract.length} more character(s)
          </p>
        )}
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="field">Field</Label>
          <Select value={selectedField} onValueChange={handleFieldChange}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder="Select a field" />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedField && availableSubcategories.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="specialization">Specialization</Label>
            <Select value={category} onValueChange={handleSpecializationChange}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Select a specialization" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {availableSubcategories.map((subcat) => (
                  <SelectItem key={subcat.id} value={subcat.id}>
                    {subcat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      
      <KeywordInput 
        keywords={keywords} 
        setKeywords={setKeywords} 
      />
      
      <AuthorsInput
        authors={additionalAuthors}
        setAuthors={setAdditionalAuthors}
      />
    </div>
  );
};

export default PaperMetadataForm;
